/**
 * «Ситуации» — четвёртый вход в ту же модель работ: к кому ехать.
 *
 * Чем этот экран НЕ является
 * --------------------------
 *
 * Не рейтингом хозяйств. В строке нет балла, доли «выполнения» и слова
 * «хуже»: строка — это НАБЛЮДЕНИЕ о данных на дату («срок прошёл, отчёта
 * нет»), а не суждение о людях. Сводной оценки субъекта в этом корпусе не
 * существует, и очередь не имеет права стать её чёрным ходом.
 *
 * Не вкладкой очереди проверки. Проверка судит ОТЧЁТ — сходится ли заявленное;
 * ситуация называет совпадение фактов по хозяйству и хранит то, как человек им
 * распорядился. Предметы разные, поэтому и адрес свой.
 *
 * Что держится формой, а не подписью
 * ----------------------------------
 *
 * * **линза — вкладка и адрес.** Взгляд передаётся ссылкой (`?lens=`), иначе
 *   «посмотри сюда» пришлось бы объяснять словами. Счётчики соседних линз
 *   приходят тем же ответом и считаются ДО сужения: выбор следующей линзы не
 *   должен быть выбором вслепую;
 * * **переход по имени хозяйства ведёт на его уровень**, а не фильтрует этот же
 *   экран (`lib/levels.ts`, правило 3);
 * * **первый взгляд — восемь строк.** Остальное уходит в раскрытие ВМЕСТЕ со
 *   своим числом: скрытое обязано быть названо;
 * * **пустое сопровождается словами.** «Сигналов по этой линзе нет» — это не
 *   «всё хорошо»: рядом печатается версия правил и порог, по которым считали;
 * * **оговорка о совпадении стоит у оснований.** Сигнал собран из фактов,
 *   сошедшихся во времени; причинности в этом нет, и сказать об этом обязан
 *   экран, а не устная договорённость;
 * * **плашка ДЕМО — от данных.** Она появляется, когда синтетика есть в
 *   основаниях, а не когда включён флаг «показывать демо»;
 * * **кнопки диспозиции — подсказка, а не право.** Разрешает запись сервер;
 *   спрятанная кнопка ничего не защищает, поэтому и не считается защитой.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAnalysisContext } from "../../analysis/context";
import {
  api,
  ApiError,
  type SituationDiff as SituationDiffData,
  type SituationList,
  type SituationRow,
} from "../../api/client";
import { useRequest } from "../../api/useRequest";
import { useAuth } from "../../auth/context";
import { PageHeader } from "../../components/AppShell";
import { Disclosure } from "../../components/Disclosure";
import { districtStep, LevelTrail } from "../../components/LevelTrail";
import { StatusMark } from "../../components/StatusMark";
import { Tab, TabList, Tabs } from "../../components/Tabs";
import { TextField } from "../../components/TextField";
import {
  Area,
  Badge,
  Button,
  Card,
  DataStatePanel,
  InlineAlert,
  RequestErrorPanel,
} from "../../components/ui";
import { DENSITY, levelPath } from "../../lib/levels";
import { ROUTES } from "../../lib/routes";
import { dateLong } from "../../astra/lib/format";
import { COMMON } from "../../i18n/common";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import features from "../features.module.css";
import styles from "./situations.module.css";
import {
  deadlineText,
  dispositionSpec,
  groundLabel,
  groundLevel,
  kindSpec,
  LENSES,
  nextGestures,
  signalText,
  syntheticRows,
} from "./situationsModel";

const m = defineMessages({
  loading: { id: 'situations.board.loading', defaultMessage: 'Считаем сигналы по хозяйствам', description: 'Заголовок заглушки загрузки очереди ситуаций' },
  loadFailed: { id: 'situations.board.loadFailed', defaultMessage: 'Очередь ситуаций не загрузилась', description: 'Заголовок панели ошибки загрузки очереди ситуаций' },
  title: { id: 'situations.board.title', defaultMessage: 'Ситуации', description: 'Заголовок экрана и последнее звено цепочки уровней: очередь «к кому ехать» (хоким, менеджер)' },
  purpose: { id: 'situations.board.purpose', defaultMessage: 'Сигналы по хозяйствам на {date}. Правила {rules}: в общую очередь попадает хозяйство, у которого есть расхождение отчёта и проверки либо не меньше {obligations, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работ на полях}} без отчёта после срока (или {area} га). Окно считается закрывающимся за {days, plural, one {# день} few {# дня} many {# дней} other {# дня}}.', description: 'Пояснение под заголовком экрана ситуаций; {date} — дата среза, {rules} — версия правил, {obligations} — порог по числу работ на полях, {area} — порог по площади в гектарах (число), {days} — за сколько дней окно считается закрывающимся' },
  toVerification: { id: 'situations.board.toVerification', defaultMessage: 'Очередь проверки', description: 'Кнопка в шапке экрана ситуаций: перейти в очередь проверки работ' },
  demoTitle: { id: 'situations.board.demo.title', defaultMessage: 'ДЕМО: часть оснований синтетическая', description: 'Заголовок предупреждения: в очереди есть строки на синтетических (демонстрационных) данных' },
  demoText: { id: 'situations.board.demo.text', defaultMessage: '{count, plural, one {# строка очереди опирается} few {# строки очереди опираются} many {# строк очереди опираются} other {# строки очереди опираются}} на синтетические записи демо-слоя. Это не факт района: заявка и отчёт в демо вышли из одного каскада, и встречи двух независимых журналов там не происходит.', description: 'Текст предупреждения о демо-данных в очереди ситуаций; {count} — сколько строк на синтетике' },
  lensLabel: { id: 'situations.board.lensLabel', defaultMessage: 'Линза очереди ситуаций', description: 'Название группы вкладок для скринридера: переключатель взгляда на очередь ситуаций' },
  lensTab: { id: 'situations.board.lensTab', defaultMessage: '{label} · {count}', description: 'Вкладка линзы: подпись и число строк в этой линзе' },
  windowEmptyTitle: { id: 'situations.board.windowEmpty.title', defaultMessage: 'В этом окне очереди строк нет', description: 'Заголовок пустого состояния, когда окно запроса за пределами очереди' },
  windowEmptyText: { id: 'situations.board.windowEmpty.text', defaultMessage: 'Показывали строки с {from}-й, а в линзе их {total}. Это обрез окна запроса, а не отсутствие сигналов.', description: 'Пояснение в пустом окне очереди; {from} — номер первой строки окна, {total} — сколько строк в линзе' },
  toStart: { id: 'situations.board.toStart', defaultMessage: 'К началу очереди', description: 'Кнопка в пустом окне очереди: вернуться к первой странице' },
  emptyTitle: { id: 'situations.board.empty.title', defaultMessage: 'Сигналов по правилу {rules} в этой линзе нет', description: 'Заголовок пустого состояния линзы; {rules} — версия правил' },
  emptyThreshold: { id: 'situations.board.empty.threshold', defaultMessage: 'Искали по порогу: {count, plural, one {# работа на поле} few {# работы на полях} many {# работ на полях} other {# работы на полях}} без отчёта после срока или {area} га.', description: 'Пояснение в пустом состоянии: по какому порогу искали; {count} — число работ на полях, {area} — площадь в гектарах (число)' },
  rowsCard: { id: 'situations.board.rowsCard', defaultMessage: 'Строк в этой линзе: {count}', description: 'Заголовок карточки со списком ситуаций; {count} — сколько строк в линзе' },
  shownOf: { id: 'situations.board.shownOf', defaultMessage: 'Показаны {shown} из {rows, plural, one {# строки} few {# строк} many {# строк} other {# строк}} линзы.', description: 'Строка под списком: показана часть строк линзы; {shown} — сколько показано, {rows} — сколько всего в окне' },
  showAll: { id: 'situations.board.showAll', defaultMessage: 'Показать все {count}', description: 'Кнопка под списком: раскрыть все строки окна; {count} — число строк' },
  windowRow: { id: 'situations.board.window.row', defaultMessage: 'Окно очереди: строка {from} из {total}.', description: 'Подпись окна очереди, когда в окне одна строка' },
  windowRows: { id: 'situations.board.window.rows', defaultMessage: 'Окно очереди: строки {from}—{to} из {total}.', description: 'Подпись окна очереди; {from}—{to} — диапазон номеров строк, {total} — всего' },
  prev: { id: 'situations.board.prev', defaultMessage: 'Предыдущие', description: 'Кнопка листания очереди ситуаций назад' },
  next: { id: 'situations.board.next', defaultMessage: 'Следующие', description: 'Кнопка листания очереди ситуаций вперёд' },
  actorUnnamed: { id: 'situations.line.actorUnnamed', defaultMessage: 'хозяйство #{id}', description: 'Имя хозяйства в строке очереди, когда имени нет; {id} — номер записи' },
  signalTitle: { id: 'situations.line.signalTitle', defaultMessage: 'Величина сигнала и его знаменатель', description: 'Подсказка по наведению на число «N из M» в строке ситуации' },
  notReviewed: { id: 'situations.line.notReviewed', defaultMessage: 'не разбирали', description: 'Бейдж в строке очереди: по ситуации ещё не было записей разбора' },
  dispWho: { id: 'situations.line.dispWho', defaultMessage: '{label} · {who}', description: 'Бейдж статуса разбора с автором; {label} — статус («заметили»), {who} — кто записал' },
  groundsTitle: { id: 'situations.line.grounds.title', defaultMessage: 'Основания и разбор', description: 'Заголовок раскрывающегося блока в строке ситуации' },
  groundsSummary: { id: 'situations.line.grounds.summary', defaultMessage: 'оснований: {count}', description: 'Краткая строка свёрнутого блока оснований; {count} — число оснований' },
  demoBadge: { id: 'situations.line.demoBadge', defaultMessage: 'демо', description: 'Бейдж у основания на синтетических данных; одно слово' },
  groundsCut: { id: 'situations.line.grounds.cut', defaultMessage: 'Показаны {shown} из {total} оснований: список обрезан бюджетом раскрытия, а не исчерпан.', description: 'Примечание под списком оснований; {shown} — показано, {total} — всего' },
  worksTitle: { id: 'situations.line.works.title', defaultMessage: 'Работы этого сигнала:', description: 'Подпись над списком работ, давших сигнал' },
  workUnnamed: { id: 'situations.line.works.unnamed', defaultMessage: 'работа #{id}', description: 'Название работы в списке, когда имени нет; {id} — номер вида работы' },
  workLine: { id: 'situations.line.works.line', defaultMessage: '{name} · {count, plural, one {# строка} few {# строки} many {# строк} other {# строки}}', description: 'Строка списка работ сигнала; {name} — название работы, {count} — её вклад в этот сигнал' },
  worksCut: { id: 'situations.line.works.cut', defaultMessage: 'Показаны {shown} из {total} работ: список обрезан бюджетом раскрытия, а не исчерпан.', description: 'Примечание под списком работ; {shown} — показано, {total} — всего' },
  toVerifyRow: { id: 'situations.line.toVerify', defaultMessage: 'Разобрать в очереди проверки', description: 'Ссылка в раскрытой строке ситуации: открыть очередь проверки этого хозяйства' },
  cannotManage: { id: 'situations.line.cannotManage', defaultMessage: 'Распорядиться ситуацией («заметили → отреагировали → закрыли») может районный участник: ситуация — это взгляд района на хозяйство.', description: 'Пояснение вместо формы разбора ситуации для пользователя без права распоряжаться (фермер, инспектор)' },
  busy: { id: 'situations.changes.busy', defaultMessage: 'Считаем…', description: 'Текст кнопки «Что изменилось», пока идёт пересчёт' },
  changes: { id: 'situations.changes.button', defaultMessage: 'Что изменилось', description: 'Кнопка в раскрытой строке: сравнить основания сейчас и на момент «заметили»' },
  changesHint: { id: 'situations.changes.hint', defaultMessage: 'Сравнение с тем, что было видно в момент «заметили».', description: 'Пояснение рядом с кнопкой «Что изменилось»' },
  changesFailed: { id: 'situations.changes.failed', defaultMessage: 'Сравнение не посчиталось', description: 'Заголовок панели ошибки сравнения оснований' },
  changesCaption: { id: 'situations.changes.caption', defaultMessage: 'Изменилось {changed} из {compared, plural, one {# величины} few {# величин} many {# величин} other {# величин}} оснований', description: 'Подпись таблицы изменений; {changed} — сколько величин изменилось, {compared} — сколько сравнили' },
  changesCaptionSnap: { id: 'situations.changes.captionSnapshot', defaultMessage: 'Изменилось {changed} из {compared, plural, one {# величины} few {# величин} many {# величин} other {# величин}} оснований; снимок сделан на {date}', description: 'Подпись таблицы изменений со снимком; {date} — дата снимка оснований' },
  colValue: { id: 'situations.changes.col.value', defaultMessage: 'Величина', description: 'Заголовок колонки таблицы изменений: название величины' },
  colBefore: { id: 'situations.changes.col.before', defaultMessage: 'Было', description: 'Заголовок колонки таблицы изменений: значение на момент «заметили»' },
  colAfter: { id: 'situations.changes.col.after', defaultMessage: 'Стало', description: 'Заголовок колонки таблицы изменений: значение сейчас' },
  colDelta: { id: 'situations.changes.col.delta', defaultMessage: 'Разность', description: 'Заголовок колонки таблицы изменений: разность стало минус было' },
  unknown: { id: 'situations.changes.unknown', defaultMessage: 'неизвестно', description: 'Значение в таблице изменений, когда величина неизвестна' },
  gestureNotice: { id: 'situations.gesture.notice', defaultMessage: 'Заметили', description: 'Кнопка формы разбора ситуации: зафиксировать, что человек увидел сигнал; коротко' },
  gestureNoticeHint: { id: 'situations.gesture.notice.hint', defaultMessage: 'Фиксирует, что человек это видит, и сохраняет снимок оснований на сейчас.', description: 'Пояснение под кнопками формы разбора, когда доступно «Заметили»' },
  gestureRespond: { id: 'situations.gesture.respond', defaultMessage: 'Отреагировали', description: 'Кнопка формы разбора ситуации: записать принятые меры; коротко' },
  gestureRespondHint: { id: 'situations.gesture.respond.hint', defaultMessage: 'Что именно сделано — словами. Без них запись неотличима от промаха по кнопке.', description: 'Пояснение под кнопками формы разбора, когда доступно «Отреагировали»' },
  gestureClose: { id: 'situations.gesture.close', defaultMessage: 'Закрыли', description: 'Кнопка формы разбора ситуации: закрыть ситуацию; коротко' },
  gestureCloseHint: { id: 'situations.gesture.close.hint', defaultMessage: 'Здесь закончено. Второй раз закрыть нельзя: след решения не переписывается.', description: 'Пояснение под кнопками формы разбора, когда доступно только «Закрыли»' },
  closedNote: { id: 'situations.form.closedNote', defaultMessage: 'Ситуация закрыта. Заново она не открывается: исправление выглядит следующей ситуацией со своими основаниями, а не правкой прошлой.', description: 'Примечание вместо формы разбора у закрытой ситуации' },
  commentLabel: { id: 'situations.form.commentLabel', defaultMessage: 'Что скажете', description: 'Подпись поля комментария в форме разбора ситуации' },
  commentPlaceholder: { id: 'situations.form.commentPlaceholder', defaultMessage: 'Например: выехали на место, полив идёт', description: 'Подсказка внутри поля комментария формы разбора ситуации' },
  commentExplain: { id: 'situations.form.commentExplain', defaultMessage: 'Слова остаются в журнале ситуации навсегда и читаются вместе с ней. «Отреагировали» и «закрыли» без слов сервер не принимает: пустая запись неотличима от случайного нажатия.', description: 'Подсказка (значок «?») у поля комментария формы разбора' },
  saveFailed: { id: 'situations.form.saveFailed', defaultMessage: 'Записать не удалось', description: 'Заголовок панели ошибки записи разбора ситуации' },
});

/** Сколько строк запрашивается за раз. Раскрытие показывает их же, а не новые. */
const PAGE = 50;

const LENS_PARAM = "lens";

export function SituationBoard() {
  const t = useT();
  const context = useAnalysisContext();
  const auth = useAuth();
  const [search, setSearch] = useSearchParams();
  const [generation, setGeneration] = useState(0);
  const [showAll, setShowAll] = useState(false);
  // Окно в очередь. Без него экран забирал первую страницу и сообщал, что
  // остальные строки «на экран не попали»: хозяйство за порогом страницы
  // просто выпадало из поля зрения экрана, чей вопрос — «к кому ехать».
  const [offset, setOffset] = useState(0);

  const lens = search.get(LENS_PARAM) ?? "all";

  const setLens = useCallback(
    (next: string) => {
      const query = new URLSearchParams(search);
      if (next === "all") query.delete(LENS_PARAM);
      else query.set(LENS_PARAM, next);
      setSearch(query, { replace: true });
      setShowAll(false);
      setOffset(0);
    },
    [search, setSearch],
  );

  // Личность входит в ключ запроса: область чтения считается от неё, и после
  // входа очередь обязана перечитаться, а не остаться анонимной.
  const identity = auth.principal?.is_authenticated
    ? (auth.principal.username ?? "")
    : "";

  // Смена среза начинает очередь заново: вторая страница ПРЕЖНЕГО среза на
  // новом означает произвольные строки — окно осталось, а очередь под ним
  // другая.
  useEffect(() => {
    setOffset(0);
    setShowAll(false);
  }, [context.viewKey, identity]);

  const queue = useRequest<SituationList>(
    () => api.situations({ ...context.viewParams, lens, limit: PAGE, offset }),
    [context.viewKey, lens, generation, identity, offset],
  );

  const reload = useCallback(() => setGeneration((count) => count + 1), []);

  const data = queue.data;
  const counts = useMemo(() => {
    const found = new Map<string, number>();
    for (const item of data?.by_lens ?? []) found.set(item.lens, item.count);
    return found;
  }, [data]);

  if (queue.state === "loading") {
    return <DataStatePanel state="loading" title={t(m.loading)} />;
  }
  if (!data) {
    return (
      <RequestErrorPanel
        error={queue.error}
        title={t(m.loadFailed)}
        action={<Button onClick={queue.reload}>{t(COMMON.retry)}</Button>}
      />
    );
  }

  const rows = data.items;
  const shown = showAll ? rows : rows.slice(0, DENSITY.tablePreview);
  const hidden = rows.length - shown.length;
  // Окно в очередь называется числами: «показаны такие-то из стольких-то».
  // Молчаливый обрез читался бы как «строк ровно столько».
  const total = data.page.total;
  const from = rows.length === 0 ? 0 : data.page.offset + 1;
  const to = data.page.offset + rows.length;
  const hasPrevious = data.page.offset > 0;
  const hasNext = to < total;
  const synthetic = syntheticRows(rows);

  return (
    <div className={features.stack}>
      <PageHeader
        title={t(m.title)}
        breadcrumb={
          // Цепочка начинается с района: очередь считает наблюдениями о
          // хозяйствах района, и своей единицы учёта у неё нет. Возврат
          // наверх — единственный жест иерархии, и он обязан быть на каждом
          // экране, иначе человек не знает, куда возвращаться.
          <LevelTrail
            steps={[districtStep(context.linkSearch())]}
            screen={t(m.title)}
          />
        }
        purpose={
          // Правила названы версией и порогом, а не словом «существенные»:
          // порог — решение владельца о внимании людей, и человек вправе знать,
          // по какому именно числу его позвали.
          t(m.purpose, {
            date: dateLong(data.horizon),
            rules: data.rules_version,
            obligations: data.materiality.obligations,
            area: data.materiality.area_ha,
            days: data.materiality.due_soon_days,
          })
        }
        actions={
          <Link to={{ pathname: ROUTES.verification, search: context.linkSearch() }}>
            <Button>{t(m.toVerification)}</Button>
          </Link>
        }
      />

      {synthetic > 0 ? (
        <InlineAlert title={t(m.demoTitle)}>
          {t(m.demoText, { count: synthetic })}
        </InlineAlert>
      ) : null}

      <Tabs selectedKey={lens} onSelectionChange={(key) => setLens(String(key))}>
        <TabList label={t(m.lensLabel)}>
          {LENSES.map((item) => (
            <Tab key={item.id} id={item.id}>
              <span title={t(item.question)}>
                {t(m.lensTab, { label: t(item.label), count: counts.get(item.id) ?? 0 })}
              </span>
            </Tab>
          ))}
        </TabList>
      </Tabs>

      {rows.length === 0 && total > 0 ? (
        /* Пустое ОКНО — это не пустая линза. Устаревший сдвиг (страница была
           третьей, а после разбора строк осталось меньше) сервер не подрезает,
           и молчаливый переход в «сигналов нет» выдал бы отсутствие в окне за
           отсутствие данных — ровно то, что канон запрещает. Поэтому здесь
           названы оба числа и дан путь назад: без него из устаревшего окна
           было не выйти, кнопки листания жили в непустой ветке. */
        <DataStatePanel state="empty" title={t(m.windowEmptyTitle)}>
          {t(m.windowEmptyText, { from: data.page.offset + 1, total })}{" "}
          <Button
            onClick={() => {
              setOffset(0);
              setShowAll(false);
            }}
          >
            {t(m.toStart)}
          </Button>
        </DataStatePanel>
      ) : rows.length === 0 ? (
        <DataStatePanel
          state="empty"
          title={t(m.emptyTitle, { rules: data.rules_version })}
        >
          {/* Слова сервера, а не свои: почему пусто — знает тот, кто считал.
              Порог повторяется рядом намеренно: «сигналов нет» без числа, по
              которому их искали, читается как «всё хорошо», а это другое
              утверждение — и его эти данные не делают. */}
          {data.empty_note}{" "}
          {t(m.emptyThreshold, {
            count: data.materiality.obligations,
            area: data.materiality.area_ha,
          })}
        </DataStatePanel>
      ) : (
        <Card title={t(m.rowsCard, { count: data.page.total })} large>
          <ul className={styles.rows}>
            {shown.map((row) => (
              <SituationLine
                key={row.key}
                row={row}
                horizon={data.horizon}
                coincidence={data.coincidence_note.text}
                canManage={auth.principal?.can_manage_situations === true}
                onChanged={reload}
              />
            ))}
          </ul>
          {hidden > 0 ? (
            <p className={styles.note}>
              {t(m.shownOf, { shown: shown.length, rows: rows.length })}{" "}
              <Button onClick={() => setShowAll(true)}>
                {t(m.showAll, { count: rows.length })}
              </Button>
            </p>
          ) : null}
          {hasPrevious || hasNext ? (
            <div className={features.rowWrap}>
              {/* Подпись окна НЕ повторяет слова бюджета плотности: рядом
                  уже стоит «Показаны 8 из 50», и второе «Показаны 1—50 из 137»
                  утверждало бы, что на экране полсотни строк. Окно называется
                  окном, бюджет — бюджетом. */}
              <p className={styles.note}>
                {from === to
                  ? t(m.windowRow, { from, total })
                  : t(m.windowRows, { from, to, total })}
              </p>
              <Button
                onClick={() => {
                  setOffset(Math.max(0, data.page.offset - PAGE));
                  setShowAll(false);
                }}
                disabled={!hasPrevious}
              >
                {t(m.prev)}
              </Button>
              <Button
                onClick={() => {
                  setOffset(data.page.offset + PAGE);
                  setShowAll(false);
                }}
                disabled={!hasNext}
              >
                {t(m.next)}
              </Button>
            </div>
          ) : null}
          <p className={styles.note}>{data.materiality_note.text}</p>
          {data.water_note ? (
            <p className={styles.note}>{data.water_note.text}</p>
          ) : null}
        </Card>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Строка очереди                                                      */
/* ------------------------------------------------------------------ */

function SituationLine({
  row,
  horizon,
  coincidence,
  canManage,
  onChanged,
}: {
  row: SituationRow;
  /** Дата среза: относительно неё срок строки либо предстоит, либо прошёл. */
  horizon: string;
  /** Оговорка о совпадении — словарная статья сервера, а не своя проза. */
  coincidence: string;
  canManage: boolean;
  onChanged: () => void;
}) {
  const t = useT();
  const context = useAnalysisContext();
  const kind = kindSpec(row.kind);
  const disposition = row.disposition ?? null;
  const spec = disposition ? dispositionSpec(disposition) : null;

  return (
    <li className={styles.row}>
      <div className={styles.head}>
        {/* Спуск идёт по ИМЕНИ единицы и ведёт на её уровень, а не фильтрует
            этот же экран: жест спуска один на весь продукт. */}
        <Link
          className={styles.actor}
          to={{
            pathname: levelPath("actor", row.actor.id),
            search: context.linkSearch(),
          }}
        >
          {row.actor.name ?? t(m.actorUnnamed, { id: row.actor.id })}
        </Link>
        <StatusMark status={kind.status} label={kind.label} title={kind.meaning} />
        <span className={styles.signal} title={t(m.signalTitle)}>
          {signalText(row)}
        </span>
        <span className={styles.area}>
          <Area microha={row.overdue_area.value} rounded />
        </span>
        <span className={styles.deadline}>{deadlineText(row.next_deadline, horizon)}</span>
        {spec ? (
          <Badge tone={spec.tone} title={disposition?.comment || undefined}>
            {spec.who ? t(m.dispWho, { label: spec.label, who: spec.who }) : spec.label}
          </Badge>
        ) : (
          <Badge tone="unknown">{t(m.notReviewed)}</Badge>
        )}
      </div>

      <Disclosure
        tone="inline"
        title={t(m.groundsTitle)}
        summary={t(m.groundsSummary, { count: row.grounds_total })}
      >
        <div className={features.stackTight}>
          <ul className={styles.grounds}>
            {row.grounds.map((ground) => {
              const level = groundLevel(ground);
              return (
              <li key={`${ground.kind}-${ground.ref_id}`}>
                {level ? (
                  // Переход по имени единицы ведёт НА ЕЁ УРОВЕНЬ, а не в
                  // фильтр этого же экрана: разбор работы живёт на работе.
                  // Срез и фильтры анализа едут со ссылкой, иначе на той
                  // стороне человек увидит другую дату, чем здесь.
                  <Link
                    to={{
                      pathname: levelPath(level, ground.ref_id),
                      search: context.linkSearch(),
                    }}
                  >
                    {groundLabel(ground)}
                  </Link>
                ) : (
                  groundLabel(ground)
                )}
                {ground.epistemic === "synthetic_demo" ? (
                  <>
                    {" "}
                    <Badge tone="demo">{t(m.demoBadge)}</Badge>
                  </>
                ) : null}
              </li>
              );
            })}
          </ul>
          {row.grounds_total > row.grounds.length ? (
            <p className={styles.note}>
              {t(m.groundsCut, { shown: row.grounds.length, total: row.grounds_total })}
            </p>
          ) : null}

          {/* Какие РАБОТЫ дали этот сигнал. Без разбивки «девять из сорока
              пяти» открывается только до обязательств, а вопрос человека —
              какой именно полив — остаётся без ответа. Число рядом с работой
              — её вклад в ЭТОТ сигнал, а не общий счёт у хозяйства, и оценкой
              работы оно не является. */}
          {row.works.length > 0 ? (
            <div className={features.stackTight}>
              <p className={styles.note}>{t(m.worksTitle)}</p>
              <ul className={styles.grounds}>
                {row.works.map((work) => (
                  // Ссылки здесь нет намеренно: `/works/:id` адресует
                  // ОБЯЗАТЕЛЬСТВО, а это правило работы в агроцикле, и своего
                  // экрана у него сегодня нет. Ссылка в никуда хуже её
                  // отсутствия: человек поверил бы, что разбор там есть.
                  <li key={work.work_def_id}>
                    {t(m.workLine, {
                      name: work.name || t(m.workUnnamed, { id: work.work_def_id }),
                      count: work.count,
                    })}
                  </li>
                ))}
              </ul>
              {row.works_total > row.works.length ? (
                <p className={styles.note}>
                  {t(m.worksCut, { shown: row.works.length, total: row.works_total })}
                </p>
              ) : null}
            </div>
          ) : null}

          {/* Оговорка стоит ЗДЕСЬ, у оснований, а не в подвале экрана: читают
              её ровно тогда, когда смотрят, из чего собран сигнал. */}
          <p className={styles.caution}>{coincidence}</p>

          <div className={features.rowWrap}>
            {/* Разбор идёт в очереди проверки: там живут отчёт и суждение по
                этому же хозяйству. Ссылка сохраняет срез и сужает очередь тем
                же хозяйством, а не «похожим фильтром». */}
            <Link
              to={{
                pathname: ROUTES.verification,
                search: context.linkSearch({
                  actor_id: row.actor.id,
                  ...(row.campaign_id ? { campaign_id: row.campaign_id } : {}),
                }),
              }}
            >
              {t(m.toVerifyRow)}
            </Link>
          </div>

          <SituationChanges row={row} />

          {canManage ? (
            <DispositionForm row={row} onChanged={onChanged} />
          ) : (
            <p className={styles.note}>{t(m.cannotManage)}</p>
          )}
        </div>
      </Disclosure>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* «Что изменилось»                                                    */
/* ------------------------------------------------------------------ */

/**
 * Сравнение снимка оснований с сегодняшним пересчётом.
 *
 * Запрашивается ПО НАЖАТИЮ, а не вместе со строкой: сравнение стоит отдельного
 * пересчёта детектора, и платить им за каждую строку первого взгляда пришлось
 * бы там, где его никто не открывал.
 */
function SituationChanges({ row }: { row: SituationRow }) {
  const t = useT();
  const context = useAnalysisContext();
  const [state, setState] = useState<"idle" | "busy" | "ready" | "error">("idle");
  const [answer, setAnswer] = useState<SituationDiffData | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = async () => {
    setState("busy");
    setError(null);
    try {
      setAnswer(await api.situationDiff(row.key, context.viewParams));
      setState("ready");
    } catch (cause) {
      setError(cause as ApiError);
      setState("error");
    }
  };

  return (
    <div className={features.stackTight}>
      <div className={features.rowWrap}>
        <Button onClick={() => void load()} disabled={state === "busy"}>
          {state === "busy" ? t(m.busy) : t(m.changes)}
        </Button>
        {state === "idle" ? (
          <span className={styles.note}>{t(m.changesHint)}</span>
        ) : null}
      </div>

      {state === "error" ? (
        <RequestErrorPanel error={error} title={t(m.changesFailed)} />
      ) : null}

      {state === "ready" && answer ? (
        <div className={features.stackTight}>
          <p className={styles.note}>{answer.note}</p>
          {answer.changes.length > 0 ? (
            <table className={features.plainTable}>
              <caption>
                {answer.snapshot_as_of
                  ? t(m.changesCaptionSnap, {
                      changed: answer.changes.length,
                      compared: answer.compared,
                      date: dateLong(answer.snapshot_as_of),
                    })
                  : t(m.changesCaption, {
                      changed: answer.changes.length,
                      compared: answer.compared,
                    })}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t(m.colValue)}</th>
                  <th scope="col">{t(m.colBefore)}</th>
                  <th scope="col">{t(m.colAfter)}</th>
                  <th scope="col">{t(m.colDelta)}</th>
                </tr>
              </thead>
              <tbody>
                {answer.changes.map((change) => (
                  <tr key={change.field}>
                    <td>{change.label}</td>
                    <td>{change.before ?? t(m.unknown)}</td>
                    <td>{change.after ?? t(m.unknown)}</td>
                    <td>
                      {change.delta === null
                        ? "—"
                        : change.delta > 0
                          ? `+${change.delta}`
                          : String(change.delta)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Диспозиция                                                          */
/* ------------------------------------------------------------------ */

const GESTURES = {
  notice: { label: m.gestureNotice, hint: m.gestureNoticeHint, requiresComment: false },
  respond: { label: m.gestureRespond, hint: m.gestureRespondHint, requiresComment: true },
  close: { label: m.gestureClose, hint: m.gestureCloseHint, requiresComment: true },
} as const;

function DispositionForm({
  row,
  onChanged,
}: {
  row: SituationRow;
  onChanged: () => void;
}) {
  const t = useT();
  const context = useAnalysisContext();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const gestures = nextGestures(row.disposition ?? null);

  const send = async (gesture: "notice" | "respond" | "close") => {
    setBusy(true);
    setError(null);
    try {
      if (gesture === "notice") {
        await api.noticeSituation(row.key, comment, context.viewParams);
      } else if (gesture === "respond") {
        await api.respondSituation(row.key, comment, context.viewParams);
      } else {
        await api.closeSituation(row.key, comment, context.viewParams);
      }
      setComment("");
      onChanged();
    } catch (cause) {
      setError(cause as ApiError);
    } finally {
      setBusy(false);
    }
  };

  if (gestures.length === 0) {
    return (
      <p className={styles.note}>{t(m.closedNote)}</p>
    );
  }

  return (
    <div className={styles.disposition}>
      <TextField
        label={t(m.commentLabel)}
        value={comment}
        onChange={setComment}
        isDisabled={busy}
        placeholder={t(m.commentPlaceholder)}
        explain={t(m.commentExplain)}
      />
      <div className={features.rowWrap}>
        {gestures.map((gesture) => {
          const spec = GESTURES[gesture];
          const blocked = spec.requiresComment && comment.trim() === "";
          return (
            <Button
              key={gesture}
              variant={gesture === "notice" ? "primary" : "secondary"}
              disabled={busy || blocked}
              onClick={() => void send(gesture)}
            >
              {t(spec.label)}
            </Button>
          );
        })}
      </div>
      <p className={styles.note}>{t(GESTURES[gestures[0]!].hint)}</p>
      {error ? (
        <RequestErrorPanel error={error} title={t(m.saveFailed)} />
      ) : null}
    </div>
  );
}
